import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const KIND_LABEL = {
  parental: "Parental consent",
  export:   "University export",
  share:    "Shared with recruiter",
};

const TARGET_TYPE_LABEL = {
  university: "University",
  recruiter:  "Recruiter",
  person:     "Person",
  self:       "You",
};

/**
 * Consent Audit — self-service view of every consent this student has
 * granted (parental, future Fast-Apply export, counsellor share). Meets
 * the GDPR SAR baseline: transparent list of what was shared with whom,
 * when, and a one-click revoke. Revoked rows stay on the page (greyed
 * out) so the audit trail is honest.
 */
export default function ConsentsPage() {
  const [rows, setRows]     = useState(null);
  const [busy, setBusy]     = useState(null);
  const [error, setError]   = useState(null);

  const load = async () => {
    setError(null);
    try {
      const r = await api.get("/profile/consents");
      setRows(r.data?.consents || []);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Could not load consents.");
    }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    setBusy(id);
    try {
      await api.post(`/profile/consents/${id}/revoke`, {});
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Could not revoke.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 pb-24" data-testid="consents-page">
      <Link to="/profile" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-6">
        <ArrowLeft className="w-4 h-4" />
        Back to profile
      </Link>

      <h1 className="text-2xl font-semibold text-stone-900 mb-1">
        Your consent record
      </h1>
      <p className="text-sm text-stone-600 mb-8 leading-relaxed">
        Everything you've explicitly agreed to share, and with whom.
        Revoke any of them at any time — the change takes effect
        immediately across our systems.
      </p>

      {error && (
        <div className="mb-4 border border-rose-200 bg-rose-50 text-rose-900 text-sm rounded-md p-3">
          {error}
        </div>
      )}
      {rows === null ? (
        <div className="flex items-center gap-2 text-stone-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="consents-empty"
          className="text-center border border-dashed border-stone-300 rounded-xl p-10"
        >
          <ShieldCheck className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <div className="text-sm font-semibold text-stone-900">Nothing shared</div>
          <div className="text-[12px] text-stone-500 mt-1">
            You haven't granted any data-sharing consents yet. This page will
            populate the first time you approve a share.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(r => {
            const revoked = !!r.revoked_at;
            return (
              <div
                key={r.consent_id}
                data-testid={`consent-row-${r.consent_id}`}
                className={`border rounded-xl p-4 ${revoked ? "border-stone-200 bg-stone-50 opacity-70" : "border-stone-300 bg-white"}`}
              >
                <div className="flex items-start gap-3">
                  {revoked
                    ? <ShieldOff className="w-5 h-5 text-stone-400 flex-none mt-0.5" />
                    : <ShieldCheck className="w-5 h-5 text-emerald-600 flex-none mt-0.5" />
                  }
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-stone-900">
                      {KIND_LABEL[r.kind] || r.kind}
                      {" · "}
                      <span className="font-normal text-stone-700">{r.target}</span>
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-stone-400 font-normal">
                        {TARGET_TYPE_LABEL[r.target_type] || r.target_type}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(r.fields_shared || []).map(f => (
                        <span
                          key={f}
                          className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 text-[11px] px-2 py-0.5"
                        >{f}</span>
                      ))}
                    </div>
                    <div className="mt-2 text-[11px] text-stone-500">
                      Granted {new Date(r.granted_at).toLocaleString()}
                      {revoked && ` · Revoked ${new Date(r.revoked_at).toLocaleString()}`}
                    </div>
                  </div>
                  {!revoked && (
                    <button
                      type="button"
                      onClick={() => revoke(r.consent_id)}
                      disabled={busy === r.consent_id}
                      data-testid={`consent-revoke-${r.consent_id}`}
                      className="flex-none border border-stone-300 hover:border-rose-400 hover:text-rose-700 text-stone-700 text-sm px-3 py-1.5 rounded-md disabled:opacity-50"
                    >
                      {busy === r.consent_id ? "Revoking…" : "Revoke"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-8 text-[11px] text-stone-500 leading-relaxed">
        We keep revoked entries here so you have a truthful audit trail. If
        you'd like a full export of the data we hold on you (GDPR Article 15),
        request one via the settings page — we'll send it within 30 days.
      </p>
    </div>
  );
}
